import React, { useEffect, useState } from 'react';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { getBusinesses, updateBusiness } from '../../services/api';
import { Store, Award, FileText, Edit, X, CheckCircle, XCircle, Star, Search } from 'lucide-react';
import { toast } from 'sonner';

const Negocios = () => {
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    loadBusinesses();
  }, []);

  const loadBusinesses = async () => {
    try {
      const response = await getBusinesses({ limit: 100 });
      setBusinesses(response.data);
    } catch (error) {
      console.error('Error loading businesses:', error);
      toast.error('Error al cargar negocios');
    } finally {
      setLoading(false);
    }
  };

  const toggleVerification = async (business) => {
    try {
      const newSettings = {
        ...business.settings,
        verification_badge: !business.settings.verification_badge
      };
      await updateBusiness(business.id, { settings: newSettings });
      toast.success(`Verificación ${newSettings.verification_badge ? 'activada' : 'desactivada'}`);
      loadBusinesses();
    } catch (error) {
      console.error('Error toggling verification:', error);
      toast.error('Error al actualizar verificación');
    }
  };

  const toggleBilling = async (business) => {
    try {
      const newSettings = {
        ...business.settings,
        billing_enabled: !business.settings.billing_enabled
      };
      await updateBusiness(business.id, { settings: newSettings });
      toast.success(`Facturación ${newSettings.billing_enabled ? 'activada' : 'desactivada'}`);
      loadBusinesses();
    } catch (error) {
      console.error('Error toggling billing:', error);
      toast.error('Error al actualizar facturación');
    }
  };

  const toggleFeatured = async (business) => {
    try {
      const newSettings = {
        ...business.settings,
        is_featured: !business.settings?.is_featured
      };
      await updateBusiness(business.id, { settings: newSettings });
      toast.success(`Destacado ${newSettings.is_featured ? 'activado' : 'desactivado'}`);
      loadBusinesses();
    } catch (error) {
      console.error('Error toggling featured:', error);
      toast.error('Error al actualizar destacado');
    }
  };

  const openEditModal = (business) => {
    setSelectedBusiness(business);
    setShowModal(true);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 tracking-tight">Gestión de Negocios</h1>
          <p className="text-slate-600 mt-2">Administra todos los negocios registrados</p>
        </div>

        {/* Buscador */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="Buscar por nombre, RFC, teléfono o email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {businesses.filter(b => {
            if (!search.trim()) return true;
            const q = search.toLowerCase();
            return (b.name || '').toLowerCase().includes(q)
              || (b.rfc || '').toLowerCase().includes(q)
              || (b.phone || '').toLowerCase().includes(q)
              || (b.email || '').toLowerCase().includes(q);
          }).length === 0 ? (
            <div className="col-span-full bg-white rounded-xl border border-slate-200 p-12 text-center">
              <Store className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">{search ? `No se encontraron negocios para "${search}"` : 'No hay negocios registrados'}</p>
            </div>
          ) : (
            businesses.filter(b => {
              if (!search.trim()) return true;
              const q = search.toLowerCase();
              return (b.name || '').toLowerCase().includes(q)
                || (b.rfc || '').toLowerCase().includes(q)
                || (b.phone || '').toLowerCase().includes(q)
                || (b.email || '').toLowerCase().includes(q);
            }).map((business) => (
              <div
                key={business.id}
                className="bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow p-6 space-y-4"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3 flex-1">
                    {business.logo_url ? (
                      <img src={business.logo_url} alt={business.name} className="w-12 h-12 rounded-lg object-cover" />
                    ) : (
                      <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                        <Store className="w-6 h-6 text-teal-700" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-bold text-slate-900 truncate">{business.name}</h3>
                      <p className="text-sm text-slate-500">{business.rfc || 'Sin RFC'}</p>
                    </div>
                  </div>
                  {business.settings?.verification_badge && (
                    <Award className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  )}
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Plan:</span>
                    <span className="font-medium text-slate-900 capitalize">{business.plan_type}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Estado:</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      business.subscription_status === 'active' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {business.subscription_status === 'active' ? 'Activo' : 'Inactivo'}
                    </span>
                  </div>
                  {business.phone && (
                    <div className="flex items-center justify-between">
                      <span className="text-slate-600">Teléfono:</span>
                      <span className="font-medium text-slate-900">{business.phone}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Creado:</span>
                    <span className="text-slate-500">{new Date(business.created_at).toLocaleDateString('es-MX')}</span>
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-100 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600 flex items-center gap-2">
                      <Award className="w-4 h-4" />
                      Verificado
                    </span>
                    <button
                      onClick={() => toggleVerification(business)}
                      className={`p-1 rounded transition-colors ${
                        business.settings?.verification_badge
                          ? 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                          : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
                      }`}
                    >
                      {business.settings?.verification_badge ? (
                        <CheckCircle className="w-5 h-5" />
                      ) : (
                        <XCircle className="w-5 h-5" />
                      )}
                    </button>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Facturación
                    </span>
                    <button
                      onClick={() => toggleBilling(business)}
                      className={`p-1 rounded transition-colors ${
                        business.settings?.billing_enabled
                          ? 'bg-green-100 text-green-600 hover:bg-green-200'
                          : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
                      }`}
                    >
                      {business.settings?.billing_enabled ? (
                        <CheckCircle className="w-5 h-5" />
                      ) : (
                        <XCircle className="w-5 h-5" />
                      )}
                    </button>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600 flex items-center gap-2">
                      <Star className="w-4 h-4" />
                      Destacado
                    </span>
                    <button
                      onClick={() => toggleFeatured(business)}
                      className={`p-1 rounded transition-colors ${
                        business.settings?.is_featured
                          ? 'bg-yellow-100 text-yellow-600 hover:bg-yellow-200'
                          : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
                      }`}
                    >
                      {business.settings?.is_featured ? (
                        <Star className="w-5 h-5 fill-yellow-500" />
                      ) : (
                        <Star className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>

                <button
                  onClick={() => openEditModal(business)}
                  className="w-full mt-4 flex items-center justify-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 text-sm font-medium transition-colors"
                >
                  <Edit className="w-4 h-4" />
                  Ver Detalles
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {showModal && selectedBusiness && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <h2 className="text-2xl font-bold text-slate-900">Detalles del Negocio</h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-500 mb-1">Nombre</label>
                  <p className="text-slate-900 font-medium">{selectedBusiness.name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-500 mb-1">RFC</label>
                  <p className="text-slate-900 font-medium">{selectedBusiness.rfc || 'N/A'}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-500 mb-1">Email</label>
                  <p className="text-slate-900">{selectedBusiness.email || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-500 mb-1">Teléfono</label>
                  <p className="text-slate-900">{selectedBusiness.phone || 'N/A'}</p>
                </div>
              </div>

              {selectedBusiness.address && (
                <div>
                  <label className="block text-sm font-medium text-slate-500 mb-1">Dirección</label>
                  <p className="text-slate-900">{selectedBusiness.address}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-500 mb-1">Plan</label>
                  <p className="text-slate-900 capitalize">{selectedBusiness.plan_type}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-500 mb-1">Estado Suscripción</label>
                  <p className={`capitalize font-medium ${
                    selectedBusiness.subscription_status === 'active' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {selectedBusiness.subscription_status}
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-500 mb-2">Funcionalidades Activas</label>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <span className="text-sm text-slate-700">Entrega a domicilio</span>
                    <span className={`text-sm font-medium ${
                      selectedBusiness.settings?.delivery_enabled ? 'text-green-600' : 'text-slate-400'
                    }`}>
                      {selectedBusiness.settings?.delivery_enabled ? 'Activo' : 'Inactivo'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <span className="text-sm text-slate-700">Recolección en sucursal</span>
                    <span className={`text-sm font-medium ${
                      selectedBusiness.settings?.pickup_enabled ? 'text-green-600' : 'text-slate-400'
                    }`}>
                      {selectedBusiness.settings?.pickup_enabled ? 'Activo' : 'Inactivo'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <span className="text-sm text-slate-700">Venta de mayoreo</span>
                    <span className={`text-sm font-medium ${
                      selectedBusiness.settings?.wholesale_enabled ? 'text-green-600' : 'text-slate-400'
                    }`}>
                      {selectedBusiness.settings?.wholesale_enabled ? 'Activo' : 'Inactivo'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-slate-200">
                <p className="text-xs text-slate-500">
                  ID: {selectedBusiness.id}<br />
                  Creado: {new Date(selectedBusiness.created_at).toLocaleString('es-MX')}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default Negocios;
